import { useMemo, useEffect, useRef, useState } from "react";
import { Check, Star, AlertTriangle } from "lucide-react";
import { siImplementationInvestment, siGetImplementationRules, resolveRegionalPrice, costPerInspectionText } from "../lib/smartInspectApi";

const FEATURE_LABELS = {
  smartinspect_operations: "SmartInspect Operations",
  smartinspect_reports: "SmartInspect Reports",
  sap_integration: "SAP Integration",
  smartinspect_insights: "SmartInspect Insights",
  priva_assistant: "PRIVA Assistant",
  standard_support: "Standard Support",
  future_product_updates: "Future Product Updates",
};

// Recommendation reads the admin package limits (single source of truth) — no
// hardcoded thresholds. Pick the smallest package whose monthly limit covers the
// volume; fall back to the unlimited/largest package.
function recommendCode(monthlyVolume, packages) {
  const visible = (packages || []).filter((p) => p.show_in_roi_calculator !== false);
  const finite = visible
    .filter((p) => !p.unlimited_inspections && p.monthly_inspection_limit != null)
    .sort((a, b) => (a.monthly_inspection_limit || 0) - (b.monthly_inspection_limit || 0));
  for (const p of finite) if (monthlyVolume <= p.monthly_inspection_limit) return p.package_code;
  const unlimited = visible.find((p) => p.unlimited_inspections);
  if (unlimited) return unlimited.package_code;
  if (finite.length) return finite[finite.length - 1].package_code;
  return visible[0]?.package_code;
}

const fmtMoney = (cur, v) => `${cur} ${Number(v).toLocaleString()}`;

// Human-readable price line for an add-on using the regional price for the
// selected Pricing Region (displayed in the selected currency).
function addonPriceLabel(a, region, currency) {
  const noFixed = a.pricing_type === "Scope Based" || a.pricing_type === "Custom";
  if (noFixed) return "Custom / quote required";
  const { amount } = resolveRegionalPrice(a.regional_pricing, region, a.price);
  if (amount === null || amount === undefined || amount === "") return "Custom / quote required";
  if (a.pricing_type === "One-Time") return `${fmtMoney(currency, amount)} one-time`;
  if (a.pricing_type === "Per Inspection") return `${fmtMoney(currency, amount)} per inspection`;
  if (a.pricing_type === "Annual") return `${fmtMoney(currency, amount)} / year`;
  return `${fmtMoney(currency, amount)} / month`;
}

// Public Step 3 package selection + auto implementation estimate. Selection only.
export function SmartInspectPackageSelect({ packages, values, setVal, annualVolume, error }) {
  const monthlyVolume = Math.round((Number(annualVolume) || 0) / 12);
  const recommended = useMemo(() => recommendCode(monthlyVolume, packages), [monthlyVolume, packages]);

  // Regional pricing context (Sprint 8P): the selected Pricing Region drives the
  // price and the selected currency drives the display label (no conversion).
  const region = values.pricing_region || "USA";
  const displayCurrency = values.preferred_currency || values.package_currency || "USD";

  const [implRules, setImplRules] = useState(null);
  useEffect(() => { siGetImplementationRules().then(setImplRules).catch(() => setImplRules([])); }, []);

  const selectedCode = values.selected_package_code;

  const buildAddonSnapshot = (addonList, names) =>
    names.map((n) => {
      const src = (addonList || []).find((x) => x.name === n);
      if (!src) return { name: n };
      const { amount } = resolveRegionalPrice(src.regional_pricing, region, src.price);
      return { name: src.name, pricing_type: src.pricing_type, price: amount, currency: displayCurrency, description: src.description };
    });

  const selectPackage = (p) => {
    const { amount: regionalSub } = resolveRegionalPrice(p.regional_pricing, region, p.monthly_subscription);
    setVal("selected_package_id", p.id);
    setVal("selected_package_code", p.package_code);
    setVal("selected_package_name", p.package_name);
    setVal("package_currency", displayCurrency);
    setVal("package_pricing_snapshot", {
      billing_model: p.billing_model,
      monthly_inspection_limit: p.monthly_inspection_limit,
      unlimited_inspections: p.unlimited_inspections,
      monthly_subscription: regionalSub,
      per_inspection_price: p.per_inspection_price,
      overage_price_per_inspection: p.overage_price_per_inspection,
      custom_pricing: p.custom_pricing,
      currency: displayCurrency,
      pricing_region: region,
      included_features: p.included_features,
      implementation_options: p.implementation_options,
    });
    setVal("package_version_timestamp", p.updated_at || new Date().toISOString());
    // Prune selected add-ons to those still active in the newly selected package.
    const nextAddons = (p.optional_addons || []).filter((a) => a.active !== false);
    const kept = (values.selected_addons || []).filter((n) => nextAddons.some((a) => a.name === n));
    setVal("selected_addons", kept);
    setVal("selected_addons_snapshot", buildAddonSnapshot(nextAddons, kept));
  };

  // Auto-select recommended package: on mount only if nothing is selected, and
  // always snap to the recommended package whenever the recommendation changes
  // (i.e. when inspection volume changes). Guarded to avoid overriding a saved
  // manual selection on reopen and to prevent infinite loops.
  const prevRec = useRef(null);
  useEffect(() => {
    if (!recommended || !(packages || []).length) return;
    const recPkg = packages.find((p) => p.package_code === recommended);
    if (!recPkg) return;
    if (prevRec.current === null) {
      prevRec.current = recommended;
      if (!selectedCode) selectPackage(recPkg);
      return;
    }
    if (prevRec.current !== recommended) {
      prevRec.current = recommended;
      selectPackage(recPkg);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recommended, packages]);

  const selectedPkg = (packages || []).find((p) => p.package_code === selectedCode);

  // When the country/pricing region changes, immediately re-snapshot the
  // selected package + add-ons so all displayed prices refresh (no recalc).
  const prevRegion = useRef(region);
  useEffect(() => {
    if (prevRegion.current === region) return;
    prevRegion.current = region;
    if (selectedPkg) selectPackage(selectedPkg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [region]);

  const addons = (selectedPkg?.optional_addons || []).filter((a) => a.active !== false)
    .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
  const selectedAddons = Array.isArray(values.selected_addons) ? values.selected_addons : [];

  const toggleAddon = (a) => {
    const isOn = selectedAddons.includes(a.name);
    const next = isOn ? selectedAddons.filter((n) => n !== a.name) : [...selectedAddons, a.name];
    setVal("selected_addons", next);
    setVal("selected_addons_snapshot", buildAddonSnapshot(addons, next));
  };

  const locations = Number(values.operating_locations) || 0;
  const implAmount = siImplementationInvestment(locations, implRules, region);
  const currency = displayCurrency;

  return (
    <div data-testid="si-package-select" className="rounded-xl border border-slate-200 bg-white p-5">
      <h3 className="text-sm font-semibold text-slate-700 mb-1 uppercase tracking-wide">SmartInspect Package</h3>
      {monthlyVolume > 0 && (
        <p className="text-xs text-slate-500 mb-4">
          Estimated monthly inspection volume: <b>{monthlyVolume.toLocaleString()}</b> (Annual ÷ 12). We've highlighted a recommended package — you can choose any option.
        </p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {packages.map((p) => {
          const isSel = p.package_code === selectedCode;
          const isRec = p.package_code === recommended;
          const feats = Object.entries(p.included_features || {}).filter(([, v]) => v).map(([k]) => FEATURE_LABELS[k] || k);
          return (
            <button
              key={p.id}
              type="button"
              data-testid={`si-package-card-${p.package_code}`}
              onClick={() => selectPackage(p)}
              className={`text-left rounded-lg border p-4 transition-all relative ${isSel ? "border-[#025979] ring-2 ring-[#025979]/30 bg-[#F0F9FF]" : "border-slate-200 hover:border-[#025979]/50 bg-white"}`}
            >
              {isRec && (
                <span data-testid={`si-package-recommended-${p.package_code}`} className="absolute top-3 right-3 inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 text-[9px] font-semibold px-2 py-0.5 rounded-full">
                  <Star className="w-2.5 h-2.5" /> Recommended
                </span>
              )}
              <div className="flex items-center gap-2">
                <div className="font-semibold text-slate-900">{p.package_name}</div>
                {isSel && <Check className="w-4 h-4 text-[#025979]" />}
              </div>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed pr-16">{p.description}</p>
              <div className="mt-3 text-sm">
                <div className="text-slate-700">
                  {p.unlimited_inspections ? "Unlimited monthly inspections" : (p.monthly_inspection_limit != null ? `Up to ${Number(p.monthly_inspection_limit).toLocaleString()} inspections per month` : "—")}
                </div>
                {(() => {
                  const { amount, missing } = resolveRegionalPrice(p.regional_pricing, region, p.monthly_subscription);
                  const showCustom = p.custom_pricing || amount == null;
                  return (
                    <>
                      <div className="font-semibold text-[#025979] mt-0.5" data-testid={`si-package-price-${p.package_code}`}>
                        {showCustom ? "Custom pricing" : `${fmtMoney(displayCurrency, amount)} per month`}
                      </div>
                      <div className="text-[10px] text-slate-400 mt-0.5" data-testid={`si-package-cpi-${p.package_code}`}>
                        {costPerInspectionText({ price: amount, monthlyLimit: p.monthly_inspection_limit, unlimited: p.unlimited_inspections, custom: showCustom, currencyCode: displayCurrency })}
                      </div>
                      {missing && !showCustom && (
                        <div data-testid={`si-package-missing-${p.package_code}`} className="mt-1 inline-flex items-center gap-1 text-[10px] text-amber-600">
                          <AlertTriangle className="w-3 h-3" /> No {region} price set — showing USA base
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
              {feats.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1">
                  {feats.slice(0, 4).map((f) => (
                    <span key={f} className="inline-flex items-center gap-1 text-[9px] text-slate-500 bg-slate-100 rounded px-1.5 py-0.5"><Check className="w-2.5 h-2.5 text-emerald-500" /> {f}</span>
                  ))}
                  {feats.length > 4 && <span className="text-[9px] text-slate-400">+{feats.length - 4} more</span>}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Optional Add-ons — driven entirely by the selected package's Admin config */}
      {addons.length > 0 && (
        <div className="mt-5" data-testid="si-addons-section">
          <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1">Optional Add-ons</h4>
          <p className="text-xs text-slate-500 mb-3">Enhance your SmartInspect subscription. Selected add-ons are included in your ROI.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {addons.map((a) => {
              const isOn = selectedAddons.includes(a.name);
              return (
                <label
                  key={a.name}
                  data-testid={`si-addon-card-${a.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className={`flex items-start gap-3 text-left rounded-lg border p-3 cursor-pointer transition-all ${isOn ? "border-[#025979] ring-2 ring-[#025979]/30 bg-[#F0F9FF]" : "border-slate-200 hover:border-[#025979]/50 bg-white"}`}
                >
                  <input
                    type="checkbox"
                    checked={isOn}
                    onChange={() => toggleAddon(a)}
                    data-testid={`si-addon-checkbox-${a.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="mt-0.5 h-4 w-4 accent-[#025979]"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-900 text-sm">{a.name}</div>
                    {a.description && <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{a.description}</p>}
                    <div className="text-xs font-semibold text-[#025979] mt-1">{addonPriceLabel(a, region, displayCurrency)}</div>
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      )}

      {/* Estimated Implementation Investment — auto-calculated from operating locations */}
      <div className="mt-5 rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid="si-implementation-estimate">
        <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1">Estimated Implementation Investment</h4>
        <p className="text-xs text-slate-500 mb-3">Based on your operating locations.</p>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <div className="text-[10px] uppercase text-slate-400">Operating Locations</div>
            <div className="font-semibold text-slate-800" data-testid="si-impl-locations">{locations.toLocaleString()}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-slate-400">Estimated Investment</div>
            <div className="font-semibold text-[#025979]" data-testid="si-impl-amount">{fmtMoney(currency, implAmount)}</div>
          </div>
        </div>
        <p className="text-[11px] text-slate-400 mt-3">Includes configuration, SAP integration, production rollout and user training.</p>
      </div>

      {error && <p data-testid="si-package-error" className="text-xs text-red-600 mt-3">{error}</p>}
    </div>
  );
}
